import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import { Map, Building2, Users, ZoomIn, ZoomOut, RefreshCw, X, Search, Info, Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SEO from '@/components/SEO';
import { getPartyConfig } from '@/lib/theme';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Party colors matching the app theme
const PARTY_COLORS = {
  labour: "#DC2626",
  conservative: "#2563EB",
  libdem: "#F97316",
  snp: "#EAB308",
  reform: "#06B6D4",
  green: "#22C55E",
  plaid: "#059669",
  plaid_cymru: "#059669",
  dup: "#991B1B",
  sinnfein: "#15803D",
  sinn_fein: "#15803D",
  independent: "#6B7280",
  noc: "#9CA3AF",
  alliance: "#F59E0B",
  sdlp: "#2E8B57",
  uup: "#003366",
  speaker: "#4B5563",
  other: "#9CA3AF"
};

// Map bounds for UK
const UK_BOUNDS = [
  [49.5, -8.5],
  [61, 2]
];

const UK_CENTER = [54.5, -3.5];

// Postcode search component
function PostcodeSearch({ onResult, mpData }) {
  const [postcode, setPostcode] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const searchPostcode = async () => {
    if (!postcode.trim()) return;
    
    setSearching(true);
    setError(null);
    setResult(null);
    
    try {
      // Use postcodes.io API - free and no auth required
      const cleanPostcode = postcode.trim().replace(/\s+/g, '').toUpperCase();
      const response = await fetch(`https://api.postcodes.io/postcodes/${cleanPostcode}`);
      const data = await response.json();
      
      if (data.status === 200 && data.result) {
        const { latitude, longitude, parliamentary_constituency } = data.result;
        
        // Find the MP for this constituency
        const mp = mpData[parliamentary_constituency];
        
        const searchResult = {
          postcode: data.result.postcode,
          constituency: parliamentary_constituency,
          lat: latitude,
          lng: longitude,
          mp: mp || null,
          region: data.result.region || data.result.country
        };
        
        setResult(searchResult);
        onResult(searchResult);
      } else {
        setError('Postcode not found. Please check and try again.');
      }
    } catch (err) {
      console.error('Postcode search error:', err);
      setError('Failed to search postcode. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      searchPostcode();
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
        <Search className="h-4 w-4 text-blue-600" />
        Find Your MP
      </h3>
      
      <div className="flex gap-2">
        <Input
          type="text"
          placeholder="Enter postcode (e.g. SW1A 1AA)"
          value={postcode}
          onChange={(e) => setPostcode(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 text-sm"
          data-testid="postcode-input"
        />
        <Button
          onClick={searchPostcode}
          disabled={searching || !postcode.trim()}
          size="sm"
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="postcode-search-btn"
        >
          {searching ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
        </Button>
      </div>
      
      {error && (
        <p className="text-red-500 text-xs mt-2">{error}</p>
      )}
      
      {result && (
        <div className="mt-3 p-3 bg-slate-50 rounded-lg">
          <p className="text-xs text-slate-500 mb-1">{result.postcode}</p>
          <p className="font-bold text-sm">{result.constituency}</p>
          {result.mp ? (
            <Link 
              to={`/politician/${result.mp.name?.toLowerCase().replace(/ /g, '-').replace(/'/g, '')}`}
              className="mt-2 block p-2 -m-2 rounded-lg hover:bg-slate-100 transition-colors group"
            >
              <p className="text-sm group-hover:text-blue-600 transition-colors">{result.mp.name}</p>
              <span 
                className="inline-block px-2 py-0.5 rounded-full text-white text-xs font-medium mt-1"
                style={{ backgroundColor: PARTY_COLORS[result.mp.party?.toLowerCase()] || PARTY_COLORS.other }}
              >
                {getPartyConfig(result.mp.party).label}
              </span>
              <span className="text-xs text-blue-500 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">View Profile →</span>
            </Link>
          ) : (
            <p className="text-xs text-slate-500 mt-1">MP data not available</p>
          )}
        </div>
      )}
    </div>
  );
}

// Component to handle map zoom and flying to location
function MapController({ flyTo, selectedFeature, onClearSelection }) {
  const map = useMap();
  
  useEffect(() => {
    if (flyTo) {
      map.flyTo([flyTo.lat, flyTo.lng], 10, { duration: 1.5 });
    }
  }, [flyTo, map]);
  
  return (
    <div className="absolute top-4 left-4 z-[1000] flex flex-col gap-2">
      <Button
        size="icon"
        variant="secondary"
        className="h-10 w-10 bg-white shadow-lg hover:bg-slate-100"
        onClick={() => map.zoomIn()}
        data-testid="map-zoom-in"
      >
        <ZoomIn className="h-5 w-5" />
      </Button>
      <Button
        size="icon"
        variant="secondary"
        className="h-10 w-10 bg-white shadow-lg hover:bg-slate-100"
        onClick={() => map.zoomOut()}
        data-testid="map-zoom-out"
      >
        <ZoomOut className="h-5 w-5" />
      </Button>
      <Button
        size="icon"
        variant="secondary"
        className="h-10 w-10 bg-white shadow-lg hover:bg-slate-100"
        onClick={() => map.fitBounds(UK_BOUNDS)}
        data-testid="map-reset"
      >
        <RefreshCw className="h-4 w-4" />
      </Button>
      {selectedFeature && (
        <Button
          size="icon"
          variant="secondary"
          className="h-10 w-10 bg-white shadow-lg hover:bg-slate-100"
          onClick={onClearSelection}
          data-testid="map-clear-selection"
        >
          <X className="h-5 w-5" />
        </Button>
      )}
    </div>
  );
}

// Constituency Map Layer
function ConstituencyLayer({ geoData, mpData, onFeatureClick, selectedFeature }) {
  const geoJsonRef = useRef(null);
  
  const style = (feature) => {
    const constituencyName = feature.properties.name;
    const mp = mpData[constituencyName];
    const party = mp?.party?.toLowerCase() || 'other';
    const isSelected = selectedFeature?.name === constituencyName;
    
    return {
      fillColor: PARTY_COLORS[party] || PARTY_COLORS.other,
      weight: isSelected ? 3 : 1,
      opacity: 1,
      color: isSelected ? '#000' : '#fff',
      fillOpacity: isSelected ? 0.9 : 0.7,
    };
  };
  
  const onEachFeature = (feature, layer) => {
    const constituencyName = feature.properties.name;
    const mp = mpData[constituencyName];
    
    layer.bindTooltip(
      `<strong>${constituencyName}</strong><br/>${mp ? `${mp.name} (${getPartyConfig(mp.party).label})` : 'No data'}`,
      { sticky: true, className: 'map-tooltip' }
    );
    
    layer.on({
      click: () => {
        onFeatureClick({
          type: 'constituency',
          name: constituencyName,
          mp: mp,
          party: mp?.party || 'other'
        });
      },
      mouseover: (e) => {
        e.target.setStyle({
          weight: 2,
          fillOpacity: 0.85
        });
      },
      mouseout: (e) => {
        if (geoJsonRef.current) {
          geoJsonRef.current.resetStyle(e.target);
        }
      }
    });
  };
  
  if (!geoData) return null;
  
  return (
    <GeoJSON
      ref={geoJsonRef}
      data={geoData}
      style={style}
      onEachFeature={onEachFeature}
    />
  );
}

// Council Map Layer
function CouncilLayer({ geoData, councilData, onFeatureClick, selectedFeature }) {
  const geoJsonRef = useRef(null);
  
  const style = (feature) => {
    const councilName = feature.properties.name;
    const council = councilData[councilName];
    const party = council?.controlling_party?.toLowerCase() || 'noc';
    const isSelected = selectedFeature?.name === councilName;
    
    return {
      fillColor: PARTY_COLORS[party] || PARTY_COLORS.noc,
      weight: isSelected ? 3 : 1,
      opacity: 1,
      color: isSelected ? '#000' : '#fff',
      fillOpacity: isSelected ? 0.9 : 0.7,
    };
  };
  
  const onEachFeature = (feature, layer) => {
    const councilName = feature.properties.name;
    const council = councilData[councilName];
    
    layer.bindTooltip(
      `<strong>${councilName}</strong><br/>${council ? getPartyConfig(council.controlling_party).label : 'No data'}`,
      { sticky: true, className: 'map-tooltip' }
    );
    
    layer.on({
      click: () => {
        onFeatureClick({
          type: 'council',
          name: councilName,
          council: council,
          party: council?.controlling_party || 'noc'
        });
      },
      mouseover: (e) => {
        e.target.setStyle({
          weight: 2,
          fillOpacity: 0.85
        });
      },
      mouseout: (e) => {
        if (geoJsonRef.current) {
          geoJsonRef.current.resetStyle(e.target);
        }
      }
    });
  };
  
  if (!geoData) return null;
  
  return (
    <GeoJSON
      ref={geoJsonRef}
      data={geoData}
      style={style}
      onEachFeature={onEachFeature}
    />
  );
}

export default function MapPage() {
  const [viewMode, setViewMode] = useState('constituencies');
  const [constituencyGeo, setConstituencyGeo] = useState(null);
  const [councilGeo, setCouncilGeo] = useState(null);
  const [mpData, setMpData] = useState({});
  const [councilData, setCouncilData] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [geoLoading, setGeoLoading] = useState(true);
  const [flyTo, setFlyTo] = useState(null);

  // Fetch GeoJSON data
  useEffect(() => {
    const fetchGeoData = async () => {
      setGeoLoading(true);
      try {
        const [constRes, councilRes] = await Promise.all([
          fetch('/geo/constituencies_simple.json'),
          fetch('/geo/councils_simple.json')
        ]);
        
        if (constRes.ok) {
          const constData = await constRes.json();
          setConstituencyGeo(constData);
        }
        
        if (councilRes.ok) {
          const councilData = await councilRes.json();
          setCouncilGeo(councilData);
        }
      } catch (error) {
        console.error('Failed to fetch GeoJSON:', error);
      } finally {
        setGeoLoading(false);
      }
    };
    
    fetchGeoData();
  }, []);

  // Fetch political data
  useEffect(() => {
    const fetchPoliticalData = async () => {
      setLoading(true);
      try {
        const [mpRes, councilRes] = await Promise.all([
          axios.get(`${API}/map/mps`),
          axios.get(`${API}/map/councils-data`)
        ]);
        
        const mpsByConstituency = {};
        (mpRes.data.mps || []).forEach(mp => {
          mpsByConstituency[mp.constituency] = mp;
        });
        setMpData(mpsByConstituency);
        
        const councilsByName = {};
        (councilRes.data.councils || []).forEach(council => {
          councilsByName[council.name] = council;
        });
        setCouncilData(councilsByName);
      } catch (error) {
        console.error('Failed to fetch political data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPoliticalData();
  }, []);

  // Handle postcode search result
  const handlePostcodeResult = (result) => {
    if (result) {
      setFlyTo({ lat: result.lat, lng: result.lng });
      setSelectedFeature({
        type: 'constituency',
        name: result.constituency,
        mp: result.mp,
        party: result.mp?.party || 'other'
      });
    }
  };

  // Calculate party breakdown for legend
  const partyBreakdown = useMemo(() => {
    const breakdown = {};
    
    if (viewMode === 'constituencies') {
      Object.values(mpData).forEach(mp => {
        const party = mp.party?.toLowerCase() || 'other';
        breakdown[party] = (breakdown[party] || 0) + 1;
      });
    } else {
      Object.values(councilData).forEach(council => {
        const party = council.controlling_party?.toLowerCase() || 'noc';
        breakdown[party] = (breakdown[party] || 0) + 1;
      });
    }
    
    return Object.entries(breakdown)
      .sort((a, b) => b[1] - a[1])
      .map(([party, count]) => ({
        party,
        count,
        color: PARTY_COLORS[party] || PARTY_COLORS.other,
        label: getPartyConfig(party).label
      }));
  }, [viewMode, mpData, councilData]);

  const handleFeatureClick = (feature) => {
    setSelectedFeature(feature);
  };

  const clearSelection = () => {
    setSelectedFeature(null);
    setFlyTo(null);
  };

  const isLoading = loading || geoLoading;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-28 md:pb-20" data-testid="map-page">
      <SEO 
        title="UK Political Map - Find Your MP by Postcode"
        description="Interactive map of UK constituencies and councils. Find your MP by postcode, explore local political representation, and view council breakdowns across Britain."
        keywords="UK constituency map, find MP by postcode, political map UK, council map, local councillors map, Westminster constituencies, UK electoral map"
      />
      
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-white/10 rounded-xl">
              <Map className="h-6 w-6 md:h-8 md:w-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-4xl font-black">Interactive UK Map</h1>
              <p className="text-slate-300 text-sm md:text-base">Click on any area to see who represents it</p>
            </div>
          </div>
        </div>
      </section>

      {/* Map Type Toggle */}
      <section className="bg-white border-b border-slate-200 sticky top-16 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-2 py-3 overflow-x-auto">
            <button
              onClick={() => { setViewMode('constituencies'); setSelectedFeature(null); setFlyTo(null); }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                viewMode === 'constituencies'
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
              data-testid="tab-constituencies"
            >
              <Users className="h-4 w-4" />
              MPs & Constituencies
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                viewMode === 'constituencies' ? 'bg-white/20' : 'bg-slate-100'
              }`}>
                {Object.keys(mpData).length || 650}
              </span>
            </button>
            <button
              onClick={() => { setViewMode('councils'); setSelectedFeature(null); setFlyTo(null); }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap ${
                viewMode === 'councils'
                  ? 'bg-slate-900 text-white'
                  : 'text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
              data-testid="tab-councils"
            >
              <Building2 className="h-4 w-4" />
              Local Councils
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                viewMode === 'councils' ? 'bg-white/20' : 'bg-slate-100'
              }`}>
                {Object.keys(councilData).length || 380}
              </span>
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-4 gap-6">
          
          {/* Map Container */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
              {isLoading ? (
                <div className="h-[450px] md:h-[550px] flex items-center justify-center bg-slate-50">
                  <div className="text-center">
                    <RefreshCw className="h-10 w-10 animate-spin text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-500">Loading map data...</p>
                  </div>
                </div>
              ) : (
                <div className="relative h-[450px] md:h-[550px]" data-testid="map-container">
                  <MapContainer
                    center={UK_CENTER}
                    zoom={6}
                    style={{ height: '100%', width: '100%' }}
                    maxBounds={UK_BOUNDS}
                    minZoom={5}
                    maxZoom={12}
                    zoomControl={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
                    />
                    
                    {viewMode === 'constituencies' && constituencyGeo && (
                      <ConstituencyLayer
                        geoData={constituencyGeo}
                        mpData={mpData}
                        onFeatureClick={handleFeatureClick}
                        selectedFeature={selectedFeature}
                      />
                    )}
                    
                    {viewMode === 'councils' && councilGeo && (
                      <CouncilLayer
                        geoData={councilGeo}
                        councilData={councilData}
                        onFeatureClick={handleFeatureClick}
                        selectedFeature={selectedFeature}
                      />
                    )}
                    
                    <MapController 
                      flyTo={flyTo}
                      selectedFeature={selectedFeature} 
                      onClearSelection={clearSelection}
                    />
                  </MapContainer>
                  
                  {/* Selected Feature Panel - Mobile overlay */}
                  {selectedFeature && (
                    <div className="lg:hidden absolute bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-lg z-[1000]">
                      <button 
                        onClick={clearSelection}
                        className="absolute top-2 right-2 p-1 hover:bg-slate-100 rounded"
                      >
                        <X className="h-5 w-5 text-slate-400" />
                      </button>
                      
                      {selectedFeature.type === 'constituency' && selectedFeature.mp && (
                        <div>
                          <h3 className="font-bold text-lg">{selectedFeature.name}</h3>
                          <p className="text-slate-600">{selectedFeature.mp.name}</p>
                          <div 
                            className="inline-block px-3 py-1 rounded-full text-white text-sm font-medium mt-2"
                            style={{ backgroundColor: PARTY_COLORS[selectedFeature.party] }}
                          >
                            {getPartyConfig(selectedFeature.party).label}
                          </div>
                        </div>
                      )}
                      
                      {selectedFeature.type === 'council' && (
                        <div>
                          <h3 className="font-bold text-lg">{selectedFeature.name}</h3>
                          <p className="text-slate-600">{selectedFeature.council?.type || 'Local Authority'}</p>
                          <div 
                            className="inline-block px-3 py-1 rounded-full text-white text-sm font-medium mt-2"
                            style={{ backgroundColor: PARTY_COLORS[selectedFeature.party] }}
                          >
                            {getPartyConfig(selectedFeature.party).label}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Map Info */}
            <div className="mt-4 flex items-start gap-2 text-sm text-slate-500">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>
                {viewMode === 'constituencies' 
                  ? 'Showing 650 Westminster Parliamentary Constituencies (2024 boundaries). Click any area to see the MP.' 
                  : 'Showing Local Authority Districts. Click any area to see council control.'}
              </p>
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            
            {/* Postcode Search - Only for constituencies view */}
            {viewMode === 'constituencies' && (
              <PostcodeSearch onResult={handlePostcodeResult} mpData={mpData} />
            )}
            
            {/* Selected Feature Info - Desktop */}
            {selectedFeature && (
              <div className="hidden lg:block bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-bold text-lg">Selected Area</h3>
                  <button 
                    onClick={clearSelection}
                    className="p-1 hover:bg-slate-100 rounded"
                    data-testid="clear-selection"
                  >
                    <X className="h-5 w-5 text-slate-400" />
                  </button>
                </div>
                
                {selectedFeature.type === 'constituency' && (
                  <div>
                    <h4 className="font-semibold text-xl mb-1">{selectedFeature.name}</h4>
                    {selectedFeature.mp ? (
                      <>
                        <p className="text-slate-600 mb-3">{selectedFeature.mp.name}</p>
                        <div 
                          className="inline-block px-4 py-2 rounded-full text-white font-medium"
                          style={{ backgroundColor: PARTY_COLORS[selectedFeature.party] }}
                        >
                          {getPartyConfig(selectedFeature.party).label}
                        </div>
                        {selectedFeature.mp.region && (
                          <p className="text-sm text-slate-500 mt-3">
                            Region: {selectedFeature.mp.region}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-slate-500">No MP data available</p>
                    )}
                  </div>
                )}
                
                {selectedFeature.type === 'council' && (
                  <div>
                    <h4 className="font-semibold text-xl mb-1">{selectedFeature.name}</h4>
                    {selectedFeature.council ? (
                      <>
                        <p className="text-slate-600 mb-3">{selectedFeature.council.type || 'Local Authority'}</p>
                        <div 
                          className="inline-block px-4 py-2 rounded-full text-white font-medium"
                          style={{ backgroundColor: PARTY_COLORS[selectedFeature.party] }}
                        >
                          {getPartyConfig(selectedFeature.party).label} Control
                        </div>
                        {selectedFeature.council.total_seats > 0 && (
                          <p className="text-sm text-slate-500 mt-3">
                            Total Seats: {selectedFeature.council.total_seats}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-slate-500">No council data available</p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Party Legend */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <h3 className="font-bold mb-4">
                {viewMode === 'constituencies' ? 'MPs by Party' : 'Councils by Control'}
              </h3>
              <div className="space-y-2.5">
                {partyBreakdown.slice(0, 10).map(({ party, count, color, label }) => (
                  <div key={party} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: color }}
                      />
                      <span className="text-sm font-medium truncate">{label}</span>
                    </div>
                    <span className="font-bold text-slate-700">{count}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-5 text-white">
              <h3 className="font-bold mb-4">Quick Stats</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-3xl font-black">{Object.keys(mpData).length || 650}</p>
                  <p className="text-sm text-slate-400">Parliamentary Seats</p>
                </div>
                <div>
                  <p className="text-3xl font-black">{Object.keys(councilData).length || 380}</p>
                  <p className="text-sm text-slate-400">Local Councils</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Custom tooltip styles */}
      <style>{`
        .map-tooltip {
          background: white;
          border: none;
          border-radius: 8px;
          padding: 8px 12px;
          font-size: 13px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        .map-tooltip strong {
          display: block;
          margin-bottom: 2px;
        }
        .leaflet-container {
          font-family: inherit;
        }
      `}</style>
    </div>
  );
}
